import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import {
    CompactListComponent,
    FeedbackTextComponent,
    SpinnerComponent,
    TabsComponent,
} from '@modules/shared/components';
import { CompactListColumn, CompactListItem } from '@modules/shared/models';
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    forkJoin,
    map,
} from 'rxjs';
import { TranslateService } from '@modules/core/services';
import { TaService } from '@modules/core/services/ta.service';
import { CustomCompactListColumnDirective } from '@modules/shared/components/compact-list/directives/custom-compact-list-column/custom-compact-list-column.directive';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
    TrafficDeviationReceipt,
    TrafficDeviationTypesUnion,
} from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { AppStateService } from '@modules/core/services/app-state.service';
import { APP_CONTEXT } from '@modules/core/models/app-state';
import { CustomDatePipe } from '@components/traffic-deviations/pipes/custom-date.pipe';
import _ from 'lodash';
import {
    MatCheckboxChange,
    MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

enum FilterType {
    garageCode = 'Garage code',
    deviation = 'Deviation',
    automaticDeviation = 'Automatic/Manual',
}

enum AutoFilterOptions {
    automatic = 'Automatic',
    manual = 'Manual',
}

interface SelectedFilters {
    deviationType: string[];
    garageCode: string[];
    automaticDeviation: string[];
}

interface DeviationProperties {
    garageCode: string;
    startDate: string;
    endDate: string;
    affected: Array<{ routeName?: string; tripNumber?: string }>;
    affectedSort: string;
    status: number;
    scope: 'DEFAULT' | 'TRIP';
    deviation?: string;
    username?: string;
    stopCentric?: boolean;
}

@Component({
    selector: 'app-traffic-deviations-select-overview',
    templateUrl: './traffic-deviations-select-overview.component.html',
    styleUrls: ['./traffic-deviations-select-overview.component.scss'],
    imports: [
        CommonModule,
        CompactListComponent,
        FeedbackTextComponent,
        SpinnerComponent,
        CustomCompactListColumnDirective,
        TranslateModule,
        MatIconModule,
        CustomDatePipe,
        TabsComponent,
        MatCheckboxModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class TrafficDeviationsSelectOverviewComponent implements OnInit {
    protected componentState: TrafficDeviationsSelectComponentState;
    protected FilterType = FilterType;
    private readonly _stateKey =
        'traffic-deviations-select-overview-component-state';
    private readonly _deviationStore = inject(TrafficDeviationsStore);
    private readonly _translate = inject(TranslateService);
    private readonly _router = inject(Router);
    private readonly _appState = inject(AppStateService);
    private readonly _tabActiveDeviations = 'active-deviations';
    private readonly _tabComingDeviations = 'coming-deviations';
    private readonly _tabExpiredDeviations = 'expired-deviations';
    private readonly _taService = inject(TaService);
    private readonly _destroyRef = inject(DestroyRef);
    protected expiredDeviationsLoading = false;
    private readonly _activeTab$ = new BehaviorSubject<string>(
        this._tabActiveDeviations,
    );
    private readonly _selectedTrafficDeviationId$ = new BehaviorSubject<string>(
        '',
    );
    private readonly _columns = new BehaviorSubject<CompactListColumn[]>([
        {
            key: 'startDate',
            name: this._translate.instant('Start time'),
            visible: true,
            width: 40,
            sortNumeric: false,
            sort: 'desc',
            template: 'customColumnTemplate',
        } as CompactListColumn,
        {
            key: 'endDate',
            name: this._translate.instant('End time'),
            visible: true,
            width: 40,
            sortNumeric: false,
            template: 'customColumnTemplate',
        } as CompactListColumn,
        {
            key: 'deviation',
            name: this._translate.instant('Deviation'),
            visible: true,
            width: 70,
            sortNumeric: false,
        } as CompactListColumn,
        {
            key: 'affected',
            name: this._translate.instant('Affected'),
            visible: true,
            width: 50,
            sortNumeric: true,
            sortByKey: 'affectedSort',
            template: 'customColumnTemplate',
        } as CompactListColumn,
        {
            key: 'status',
            name: 'Status',
            visible: true,
            width: 10,
            sortNumeric: true,
            template: 'customColumnTemplate',
        } as CompactListColumn,
    ]);
    private readonly _filters$ = new BehaviorSubject<Map<FilterType, string[]>>(
        new Map([
            [FilterType.deviation, []],
            [FilterType.garageCode, []],
            [FilterType.automaticDeviation, []],
        ]),
    );
    private readonly _selectedFilters$ = new BehaviorSubject<SelectedFilters>({
        deviationType: [],
        garageCode: [],
        automaticDeviation: [AutoFilterOptions.manual],
    });
    private readonly _autoTATCreatedBy = 'OMS.DT';

    protected readonly columns$ = this._columns
        .asObservable()
        .pipe(distinctUntilChanged());

    protected readonly trafficDeviations$ = combineLatest([
        this._deviationStore.trafficDeviations$,
        this._deviationStore.trafficDeviationReceipts$,
        this._deviationStore.expiredTrafficDeviations$,
        this._selectedFilters$,
        this._activeTab$,
        this._selectedTrafficDeviationId$,
    ]).pipe(
        map(([trafficDeviations, receipts, expiredDeviations, selectedFilters]) => {
            const isExpiredTab = this.componentState?.selectedTab === this._tabExpiredDeviations;

            const sourceDeviations: TrafficDeviationTypesUnion[] = isExpiredTab
                ? (expiredDeviations as TrafficDeviationTypesUnion[])
                : trafficDeviations.filter(this.filterActiveAndComingDeviations);

            let mappedDeviations = sourceDeviations.map((d) =>
                this._compactListItemMapper(
                    d,
                    receipts.filter((r) => r.trafficDeviationId === d.id),
                ),
            );

            // Apply selected filters if any exist
            Object.entries(FilterType).forEach(([key, filterType]) => {
                const selectedValues =
                    selectedFilters[key as keyof SelectedFilters];
                if (selectedValues && selectedValues.length > 0) {
                    mappedDeviations = mappedDeviations.filter((deviation) => {
                        if (filterType === FilterType.automaticDeviation) {
                            return this.filterAutomaticDeviations(
                                deviation,
                                selectedValues,
                            );
                        }
                        return selectedValues.includes(
                            deviation.properties[
                                this.getFilterTypeKey(filterType)
                            ],
                        );
                    });
                }
            });

            // Update available filter options
            const filters = new Map<FilterType, string[]>([
                [
                    FilterType.deviation,
                    [
                        ...new Set(
                            sourceDeviations.map((item) =>
                                this._translate.instant(item.type),
                            ),
                        ),
                    ],
                ],
                [
                    FilterType.garageCode,
                    [
                        ...new Set(
                            sourceDeviations.map(
                                (item) => item.garageCode,
                            ),
                        ),
                    ],
                ],
                [
                    FilterType.automaticDeviation,
                    [AutoFilterOptions.automatic, AutoFilterOptions.manual],
                ],
            ]);
            this._filters$.next(filters);

            return mappedDeviations;
        }),
    );

    get IsWideWindowWidth(): boolean {
        return (
            +this._appState.stateSnapshot.selectionOverviewWidthByContext[
                APP_CONTEXT.TRAFFIC_DEVIATIONS
            ] > 280
        );
    }

    ngOnInit(): void {
        this.componentState = this._getComponentState();
        this._selectedFilters$.next(this.componentState.selectedFilters);
        if (this.componentState.selectedTab === this._tabExpiredDeviations) {
            this._loadExpiredDeviations();
        }
        this._deviationStore.deviationPutSuccess$.pipe(
            takeUntilDestroyed(this._destroyRef),
        ).subscribe(() => {
            if (this.componentState.selectedTab === this._tabExpiredDeviations) {
                this._loadExpiredDeviations();
            }
        });
    }

    protected getFilterTypeKey(value: string): string {
        return Object.keys(FilterType).find(
            (key) => FilterType[key as keyof typeof FilterType] === value,
        );
    }

    protected isFilterSelected(
        filterType: keyof SelectedFilters,
        value: string,
    ): boolean {
        return this._selectedFilters$.value[filterType]?.includes(value);
    }

    protected setFilter(
        filterType: keyof SelectedFilters,
        value: string,
        event: MatCheckboxChange,
    ): void {
        if (event.checked) {
            this.addFilter(filterType, value);
        } else {
            this.removeFilter(filterType, value);
        }
        this._updateComponentState();
    }

    protected clearFilter() {
        this._selectedFilters$.next({
            deviationType: [],
            garageCode: [],
            automaticDeviation: [],
        });
        this._updateComponentState();
    }

    private filterAutomaticDeviations(
        deviation: any,
        selectedValues: string[],
    ): boolean {
        if (
            selectedValues.includes(AutoFilterOptions.automatic) &&
            selectedValues.includes(AutoFilterOptions.manual)
        ) {
            return true;
        }
        if (selectedValues.includes(AutoFilterOptions.automatic)) {
            return deviation.properties['username'] === this._autoTATCreatedBy;
        }
        if (selectedValues.includes(AutoFilterOptions.manual)) {
            return deviation.properties['username'] !== this._autoTATCreatedBy;
        }
        return false;
    }

    private addFilter(type: keyof SelectedFilters, value: string) {
        const currentValues = this._selectedFilters$.value[type] ?? [];
        if (!currentValues.includes(value)) {
            this.updateFilter(type, [...currentValues, value]);
        }
    }

    private removeFilter(type: keyof SelectedFilters, value: string) {
        const currentValues = this._selectedFilters$.value[type];
        this.updateFilter(
            type,
            currentValues.filter((v) => v !== value),
        );
    }

    private updateFilter(type: keyof SelectedFilters, values: string[]) {
        this._selectedFilters$.next({
            ...this._selectedFilters$.value,
            [type]: values,
        });
    }

    private filterActiveAndComingDeviations = (
        deviation: TrafficDeviationTypesUnion,
    ): boolean => {
        const startDate = this.getEarliestDate(
            deviation.trafficDeviationScope.startDate,
            deviation.trafficDeviationScope.trafficDeviationInformation
                ?.publicationStartDate,
        );
        const endDate = this.getLatestDate(
            deviation.trafficDeviationScope.endDate,
            deviation.trafficDeviationScope.trafficDeviationInformation
                ?.publicationEndDate,
        );
        const currentTime = Date.now();

        if (this.componentState.selectedTab === this._tabActiveDeviations) {
            // Show if startDate is past endDate is future
            return (
                startDate.getTime() <= currentTime &&
                endDate.getTime() >= currentTime
            );
        } else if (
            this.componentState.selectedTab === this._tabComingDeviations
        ) {
            // Show if startDate is in the future
            return startDate.getTime() > currentTime;
        } else {
            return false;
        }
    };

    private getEarliestDate(
        startDate: string,
        publicationStartDate: string | null,
    ): Date {
        const startDateTime = new Date(startDate);
        const publicationStartDateTime = publicationStartDate
            ? new Date(publicationStartDate)
            : null;
        if (!publicationStartDateTime) return startDateTime;
        return new Date(
            Math.min(
                publicationStartDateTime.getTime(),
                startDateTime.getTime(),
            ),
        );
    }

    private getLatestDate(
        endDate: string,
        publicationEndDate: string | null,
    ): Date {
        const endDateTime = new Date(endDate);
        const publicationEndDateTime = publicationEndDate
            ? new Date(publicationEndDate)
            : null;
        if (!publicationEndDateTime) return endDateTime;
        return new Date(
            Math.max(publicationEndDateTime.getTime(), endDateTime.getTime()),
        );
    }

    protected itemSelected(item: CompactListItem): void {
        const encodedItemId = encodeURIComponent(item.id);
        const routerUrl = this._router.routerState.snapshot.url;
        const currentItemOpen = routerUrl.indexOf(
            `traffic-deviation/${encodedItemId}`,
        );

        if (currentItemOpen === -1) {
            this._deviationStore.updateTrafficDeviationState({
                deviationScope: item.properties['scope'],
            });
            this._router.navigate([
                '',
                {
                    outlets: {
                        outlet0: ['traffic-deviation', item.id],
                        outlet1: null,
                        outlet2: null,
                    },
                },
            ]);
            this._selectedTrafficDeviationId$.next(<string>item.id);
        } else {
            this._deviationStore.updateTrafficDeviationState({
                deviationScope: 'DEFAULT',
            });
            this._router.navigate([
                '',
                { outlets: { outlet0: null, outlet1: null, outlet2: null } },
            ]);
            this._selectedTrafficDeviationId$.next('');
        }
    }

    protected columnsUpdated(columns: CompactListColumn[]): void {
        this._columns.next(columns);
    }

    private _compactListItemMapper = (
        deviation: TrafficDeviationTypesUnion,
        receipts: TrafficDeviationReceipt[],
    ): CompactListItem => {
        const trafficDeviationInformation =
            deviation.trafficDeviationScope.trafficDeviationInformation;
        let properties = {} as DeviationProperties;

        switch (deviation.type) {
            case 'ChangeOfJourneyPattern':
                properties = {
                    garageCode: deviation.garageCode,
                    startDate: deviation.trafficDeviationScope.startDate,
                    endDate: deviation.trafficDeviationScope.endDate,
                    affected: deviation.trafficDeviationScope.routeInfo,
                    affectedSort: `${deviation.trafficDeviationScope.routeInfo[0].routeName ?? ''}${deviation.trafficDeviationScope.routeInfo[0].tripNumber ?? ''}`,
                    status: this._getStatus(receipts),
                    scope: 'DEFAULT',
                };
                break;
            case 'PredictionInaccurate':
                properties = {
                    garageCode: deviation.garageCode,
                    startDate: deviation.trafficDeviationScope.startDate,
                    endDate: deviation.trafficDeviationScope.endDate,
                    affected: [
                        {
                            routeName:
                                deviation.trafficDeviationScope.routeInfo[0]
                                    .routeName,
                        },
                    ],
                    affectedSort: `${deviation.trafficDeviationScope.routeInfo[0].routeName}`,
                    status: this._getStatus(receipts),
                    scope: 'TRIP',
                };
                break;
            case 'ChangeOfJourneyTiming':
                properties = {
                    garageCode: deviation.garageCode,
                    startDate: deviation.trafficDeviationScope.startDate,
                    endDate: deviation.trafficDeviationScope.endDate,
                    affected: [
                        {
                            tripNumber:
                                deviation.trafficDeviationScope.routeInfo[0]
                                    .tripNumber,
                            routeName:
                                deviation.trafficDeviationScope.routeInfo[0]
                                    .routeName,
                        },
                    ],
                    affectedSort: `${deviation.trafficDeviationScope.routeInfo[0].routeName ?? ''}${deviation.trafficDeviationScope.routeInfo[0].tripNumber ?? ''}`,
                    status: this._getStatus(receipts),
                    scope: 'TRIP',
                    username: deviation.createdBy,
                };
                break;
            case 'DeviationInformation':
                properties = {
                    garageCode: deviation.garageCode,
                    startDate: deviation.trafficDeviationScope.startDate,
                    endDate: deviation.trafficDeviationScope.endDate,
                    affected: deviation.trafficDeviationScope.routeInfo,
                    status: this._getStatus(receipts),
                    scope:
                        deviation.trafficDeviationScope.routeInfo.length ===
                            1 &&
                        !!deviation.trafficDeviationScope.routeInfo?.[0]
                            .tripNumber
                            ? 'TRIP'
                            : 'DEFAULT',
                    affectedSort: `${deviation.trafficDeviationScope.routeInfo?.[0].routeName ?? ''}`,
                    stopCentric:
                        trafficDeviationInformation?.stopScope ===
                        'StopCentric',
                };
                break;
            case 'ConnectionCancellation':
                properties = {
                    garageCode: deviation.garageCode,
                    startDate: deviation.trafficDeviationScope.startDate,
                    endDate: deviation.trafficDeviationScope.endDate,
                    deviation: deviation.type,
                    affected: [
                        {
                            tripNumber:
                                deviation.trafficDeviationScope.routeInfo[0]
                                    .tripNumber,
                            routeName:
                                deviation.trafficDeviationScope.routeInfo[0]
                                    .routeName,
                        },
                    ],
                    affectedSort: `${deviation.trafficDeviationScope.routeInfo[0].routeName ?? ''}${deviation.trafficDeviationScope.routeInfo[0].tripNumber ?? ''}`,
                    status: this._getStatus(receipts),
                    scope: 'TRIP',
                };
                break;
        }

        return {
            id: deviation.id,
            type: deviation.type,
            selected: this._selectedTrafficDeviationId$.value === deviation.id,
            properties: {
                ...properties,
                deviation: this._translate.instant(deviation.type),
            } as any,
            highlighted: false,
            semiHighlighted: false,
            criticalHighlighted: properties?.status >= 4,
            muted: false,
        } as CompactListItem;
    };

    private _getStatus(receipts: TrafficDeviationReceipt[]) {
        if (!receipts.length) return 0;

        const statuses = receipts.map((r) => r.status);
        return Math.max(...statuses);
    }

    protected openNewTrafficDeviation() {
        this._selectedTrafficDeviationId$.next('');
        this._router.navigate(['', { outlets: { outlet0: null } }]).then(() => {
            this._appState.changeContext(APP_CONTEXT.NEW_TRAFFIC_DEVIATION);
        });
    }

    protected async changeTab(selectedTab: string) {
        this.componentState.selectedTab = selectedTab;
        this._updateComponentState();
        this._activeTab$.next(selectedTab);
        if (selectedTab === this._tabExpiredDeviations) {
            this._loadExpiredDeviations();
        }
    }

    private _loadExpiredDeviations(): void {
        const garages = this._appState.stateSnapshot.garages?.filter((g) =>
            this._appState.stateSnapshot.selectedGarageCodes?.includes(g.code)
        ) ?? [];
        if (!garages.length) return;

        this.expiredDeviationsLoading = true;
        const maxExpiredDate = new Date(Date.now() - 24 * 60 * 60 * 1000);

        forkJoin(
            garages.map((g) => forkJoin({
                deviations: this._taService.getExpiredTrafficDeviations(g.code, maxExpiredDate),
                receipts: this._taService.getExpiredTrafficDeviationReceipts(g.code, maxExpiredDate),
            }))
        ).subscribe({
            next: (results) => {
                this._deviationStore.setExpiredTrafficDeviations(
                    results.flatMap((r) => r.deviations),
                );
                this._deviationStore.updateTrafficDeviationReceipt(
                    results.flatMap((r) => r.receipts),
                );
                this.expiredDeviationsLoading = false;
            },
            error: () => {
                this.expiredDeviationsLoading = false;
            },
        });
    }

    private _getComponentState(): TrafficDeviationsSelectComponentState {
        const storedState = localStorage.getItem(this._stateKey);
        const state = new TrafficDeviationsSelectComponentState();
        state.selectedTab = this._tabActiveDeviations;
        state.tabs = [this._tabActiveDeviations, this._tabComingDeviations, this._tabExpiredDeviations];

        // Get the selected tab from the localstorage and set as active if the tab exists in the tabs-list
        if (storedState) {
            const storedVehicleComponentState: TrafficDeviationsSelectComponentState =
                JSON.parse(storedState);
            const storedSelectedTab = storedVehicleComponentState.selectedTab;
            if (_.includes(state.tabs, storedSelectedTab)) {
                state.selectedTab = storedSelectedTab;
            }
            state.selectedFilters = storedVehicleComponentState.selectedFilters;
        }
        return state;
    }

    private _updateComponentState() {
        this.componentState.selectedFilters = this._selectedFilters$.value;
        localStorage.setItem(
            this._stateKey,
            JSON.stringify(this.componentState),
        );
    }
}

export class TrafficDeviationsSelectComponentState {
    tabs: string[];
    selectedTab: string;
    selectedFilters: SelectedFilters = {
        deviationType: [],
        garageCode: [],
        automaticDeviation: [AutoFilterOptions.manual],
    } as SelectedFilters;
    trafficDeviationsColumns: CompactListColumn[];
}
